/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { useRecoilValueLoadable } from "recoil";
import { evidenceAtom } from "@state/State";
import { IEvidence, ProgramData, ProgressCheck } from "@interfaces/Interfaces";
import { subYears } from "date-fns";
import SegmentedProgressBar from "@components/common/SegmentedProgressBar";
import * as EvidenceUtils from "@utils/evidenceUtils";
import * as ProgramUtils from "@utils/programUtils";
import { LEARNING_OUTCOMES_IDS } from "@constants/programs";

function OverviewWithOngoingDevelopment({
  program,
  ongoingDevelopmentProgressCheckId,
  generalCompId,
  paraPassLearningCompId,
}: {
  program: ProgramData;
  ongoingDevelopmentProgressCheckId: string;
  generalCompId: string;
  paraPassLearningCompId: string;
}) {
  const evidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);

  const programEvidence: IEvidence[] = getProgramEvidence();
  const draftEvidenceCount = getDraftCount();
  const progressChecksMapped = buildProgressCheckMap();

  function getProgramEvidence(): IEvidence[] {
    if (!evidence) return [];

    const result =
      evidence.contents?.filter((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const programName = evidenceJSON?.programInfo?.program || "";

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        return (
          programName === program.Name &&
          onHoldReset !== 1 &&
          evidence.draft !== true &&
          new Date(evidence.date) >= subYears(new Date(), 2)
        );
      }) ?? [];

    return result;
  }

  function buildProgressCheckMap(): {
    id: string;
    title: string;
    numerator: number;
    denominator: number;
    status: string;
  }[] {
    const result: { id: string; title: string; numerator: number; denominator: number; status: string }[] = [];
    program.ProgressChecks?.map((pc, i) => {
      const status = ProgramUtils.getProgressCheckLabelInformationForTelTriage(
        pc,
        programEvidence,
        program.ProgressChecks?.[i - 1] ?? undefined
      ).status;
      const id = pc.ID;
      let title = "";
      let numerator = 0;
      let denominator = 0;
      if (pc.ID === ongoingDevelopmentProgressCheckId) {
        title = "Ongoing evidence this month";
        numerator = getCompsDoneForOngoingDevelopmentPC();
        denominator = 2;
      } else {
        title =
          status === "Completed"
            ? `${program.Title ?? ""} ${pc.Name.toLowerCase()} completed`
            : `Mandatory ${pc.Name} competencies completed`;
        numerator = getCompsDoneForRegularPCs(pc);
        denominator = pc["Competence/Activity"].filter((comp) => comp["Number Evidences required"]).length;
      }

      result.push({ id, title, numerator, denominator, status });
    });

    return result;
  }

  function getDraftCount(): number {
    if (!evidence) return 0;

    const result: any[] =
      evidence.contents?.filter((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const programName = evidenceJSON?.programInfo?.program || "";

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        return (
          programName === program.Name &&
          onHoldReset !== 1 &&
          evidence.draft === true &&
          new Date(evidence.date) >= subYears(new Date(), 2)
        );
      }) ?? [];

    return result.length;
  }

  function getCompsDoneForOngoingDevelopmentPC() {
    const groupedEvidenceForOngoingDevelopment = programEvidence
      .filter((e) => {
        const parsedEvidence = e.evidenceJSON;
        let peerReviewDone = false;
        if (ProgramUtils.isCHUB(program.ID)) {
          peerReviewDone = parsedEvidence?.LearningOutcomes?.find(
            (lo: any) => lo.ID === LEARNING_OUTCOMES_IDS.LAS_CHUB_ONGOING_DEVELOPMENT_LO // Peer review LO ID for LAS CHUB
          );
        } else if (ProgramUtils.isClinicalAdvisor(program.ID)) {
          peerReviewDone =
            parsedEvidence?.programInfo?.progressCheckCompetenceID ===
            LEARNING_OUTCOMES_IDS.LAS_CA_ONGOING_DEVELOPMENT_LO; // Peer Review comp ID for LAS CA
        }
        return (
          !parsedEvidence?.OnHoldReset &&
          !e.draft &&
          (parsedEvidence?.programInfo?.progressCheckCompetenceID == generalCompId ||
            parsedEvidence?.programInfo?.progressCheckCompetenceID == paraPassLearningCompId ||
            peerReviewDone)
        );
      })
      .reduce((acc: { [key: string]: IEvidence[] }, current) => {
        let d = new Date();
        if (
          current.evidenceJSON.programInfo?.progressCheckCompetenceID ===
          LEARNING_OUTCOMES_IDS.LAS_CA_ONGOING_DEVELOPMENT_LO
        ) {
          d = new Date(current.evidenceJSON["Date of Call"]);
        } else if (
          current.evidenceJSON.programInfo?.progressCheckCompetenceID === paraPassLearningCompId ||
          current.evidenceJSON.programInfo?.progressCheckCompetenceID === generalCompId
        ) {
          d = new Date(current.evidenceJSON["Date of activity"]);
        } else {
          d = new Date(current.date);
        }
        const month = d.toLocaleString("default", { month: "long" });

        if (!acc[month]) {
          acc[month] = [];
        }
        acc[month].push(current);
        return acc;
      }, {});
    const currentMonth = new Date().toLocaleString("default", { month: "long" });
    const ongoingDevelopmentForThisMonth = groupedEvidenceForOngoingDevelopment[currentMonth];

    return ongoingDevelopmentForThisMonth?.length ?? 0;
  }

  function getCompsDoneForRegularPCs(pc: ProgressCheck) {
    let compsDone = 0;
    pc["Competence/Activity"].forEach((comp) => {
      const evidenceStatus = EvidenceUtils.hasReachedEvidenceCount(programEvidence, comp);
      const learningOutcomesStatus = EvidenceUtils.hasLearningOutcomes(programEvidence, comp);
      if (
        comp["Number Evidences required"] &&
        evidenceStatus === "COMPLETE" &&
        (learningOutcomesStatus === "COMPLETE_ALL" ||
          learningOutcomesStatus === "COMPLETE_REQUIRED" ||
          !comp["Learning Objective"]?.length)
      ) {
        compsDone += 1;
      }
    });

    return compsDone;
  }

  return (
    <div>
      {progressChecksMapped.map((item, i) => {
        return (
          <div
            className="mt-4"
            key={i}
          >
            {item.status === "Completed" ? (
              <div className={`text-grey-90 text-acc-13px`}>{item.title}</div>
            ) : (
              <SegmentedProgressBar
                title={item.title}
                numerator={item.numerator}
                denominator={item.denominator}
              />
            )}
          </div>
        );
      })}
      <div className="flex mt-4">
        {progressChecksMapped
          .filter((pc) => pc.id !== ongoingDevelopmentProgressCheckId)
          .some((pc) => pc.numerator < pc.denominator) && (
          <div className="flex-1 flex flex-col justify-between">
            <div className="text-grey-90 text-acc-13px font-normal mb-2">Evidence added</div>
            <div className="text-program-card-eprr-text text-acc-28px font-bold">
              {programEvidence ? programEvidence.length : 0}
            </div>
          </div>
        )}
        <div className="flex-1 flex flex-col justify-between items-start">
          <div className="mb-2 rounded-xl bg-draft-badge text-draft-badge-text px-2 py-1 text-acc-12px font-semibold">
            Draft
          </div>
          <div className="text-program-card-eprr-text text-acc-28px font-bold">{draftEvidenceCount}</div>
        </div>
      </div>
    </div>
  );
}

export default OverviewWithOngoingDevelopment;
